import { defineStore } from "pinia";
import SettingResource from "~/models/mtss/settings/Setting";
import StudentResource from "~/models/mtss/Student";
import StatisticResource from "~/models/mtss/Statistic";
import ActivityResource from "~/models/mtss/Activity";
import UpdateResource from "~/models/mtss/Update";
import ScheduleResource from "~/models/mtss/Schedule";

import SupportInterventionResource from "~/models/mtss/settings/SupportIntervention";
import AssessmentResource from "~/models/mtss/settings/Assessment";
import CategoryResource from "~/models/mtss/settings/Category";
import CommunicationDescriptionResource from "~/models/mtss/settings/CommunicationDescription";
import ProviderResource from "~/models/mtss/settings/Provider";
import StatusResource from "~/models/mtss/settings/Status";
import ContactPersonResource from "~/models/mtss/settings/ContactPerson";

export const useMtssStore = defineStore("mtss", {
  state: () => ({
    loading_student: false,
    current_student: null,
    current_student_mtss: {
      statistics: null,
      activities: [],
      communications: [],
      progress: [],
      schedule: [],
    },
    privacy_levels: ["Normal", "Confidential"],
    activity_types: [],
    update_types: [],
    assessments: [],
    categories: [],
    communication_descriptions: [],
    contact_people: [],
    providers: [],
    statuses: [],
    supports_interventions: [],
  }),
  getters: {
    is_mtss_admin: () => {
      const auth = useAuthStore();
      return auth.user.hasRoleIn(["mtss-admin", "tech"]);
    },
  },
  actions: {
    async loadConfig() {
      const response = await SettingResource.find("metadata");
      console.log("mtss-store-config", response);
      this.setMtssStudent(
        response.current_student,
        response.current_student_mtss
      );
      this.activity_types = response.activity_types;
      this.update_types = response.update_types;
      this.assessments = response.assessments.map(
        (item) => new AssessmentResource(item)
      );
      this.categories = response.categories.map(
        (item) => new CategoryResource(item)
      );
      this.communication_descriptions = response.communication_descriptions.map(
        (item) => new CommunicationDescriptionResource(item)
      );
      this.contact_people = response.contact_people.map(
        (item) => new ContactPersonResource(item)
      );
      this.providers = response.providers.map(
        (item) => new ProviderResource(item)
      );
      this.statuses = response.statuses.map((item) => new StatusResource(item));
      this.supports_interventions = response.supports_interventions.map(
        (item) => new SupportInterventionResource(item)
      );
    },
    async loadMtssStudent(studentId) {
      console.log("mtss-store", studentId);
      try {
        if (
          this.current_student !== null &&
          this.current_student.STUDENT_ID == studentId.trim()
        ) {
          console.log("skip", studentId);
          return;
        }
        this.loading_student = true;
        const response = await StudentResource.find(studentId.trim());
        console.log("set", studentId, response);
        this.setMtssStudent(response.data, response);
        this.loading_student = false;
      } catch (error) {
        this.current_student = null;
        this.current_student_mtss.statistics = null;
        this.current_student_mtss.activities = [];
        this.current_student_mtss.communications = [];
        this.current_student_mtss.progress = [];
        this.current_student_mtss.schedule = [];
        this.loading_student = false;
        useApiResponseHandler().error(error, 'Failed to load student');
        throw error;
      }
    },

    setMtssStudent(studentData, mtssData) {
      if (studentData) {
        this.current_student = new StudentResource(studentData);
        this.current_student_mtss.statistics = studentData.mtss_statistics
          ? new StatisticResource(studentData.mtss_statistics)
          : null;
      }

      if (mtssData) {
        this.current_student_mtss.activities = mtssData.activities.map(
          (a) => new ActivityResource(a)
        );
        this.current_student_mtss.communications = mtssData.communications.map(
          (a) => new UpdateResource(a)
        );
        this.current_student_mtss.progress = mtssData.progress.map(
          (a) => new UpdateResource(a)
        );
        this.current_student_mtss.schedule = mtssData.schedule.map(
          (a) => new ScheduleResource(a)
        );
      }
    },

    getActiveOptions(optionField) {
      return this[optionField].filter((item) => item.active == "Y");
    },

    isCurrentUser(username) {
      const auth = useAuthStore();
      return (
        auth.user.username.trim().toLowerCase() ===
        username.trim().toLowerCase()
      );
    },

    /**
     *  Handle Changes to Related Data
     **/
    updateRelatedData(model, myKey, matchFn) {
      if (typeof this[myKey] != "object" || !Array.isArray(this[myKey])) {
        // Don't care about save.
        return;
      }

      const dataIndex = this[myKey].findIndex(matchFn);
      if (dataIndex == -1) {
        this[myKey].push(model);
      } else {
        this[myKey].splice(dataIndex, 1, model);
      }
    },

    removeRelatedData(model, myKey, matchFn) {
      if (typeof this[myKey] != "object" || !Array.isArray(this[myKey])) {
        // Don't care about save.
        return;
      }

      const dataIndex = this[myKey].findIndex(matchFn);
      if (dataIndex != -1) {
        this[myKey].splice(dataIndex, 1);
      }
    },

    updateStudentActivity(activity) {
      if (
        activity.student_id.trim() !== this.current_student.STUDENT_ID.trim()
      ) {
        return;
      }
      const index = this.current_student_mtss.activities.findIndex((a) => {
        return a.id == activity.id;
      });
      if (index === -1) {
        this.current_student_mtss.activities.push(activity);
      } else {
        this.current_student_mtss.activities.splice(index, 1, activity);
      }
    },

    updateStudentUpdate(update) {
      if (update.student_id.trim() !== this.current_student.STUDENT_ID.trim()) {
        return;
      }

      // Check if update belongs to activity. If so, update the activity.
      if (update.activity_id !== null) {
        const index = this.current_student_mtss.activities.findIndex((a) => {
          return a.id == update.activity_id;
        });
        if (index !== -1) {
          const uIndex = this.current_student_mtss.activities[
            index
          ].updates.findIndex((u) => {
            return u.id == update.id;
          });

          if (uIndex === -1) {
            this.current_student_mtss.activities[index].updates.push(update);
          } else {
            this.current_student_mtss.activities[index].updates.splice(
              uIndex,
              1,
              update
            );
          }
        }
        return;
      }

      // Update belongs to student.
      if (update.update_type == "Communication") {
        const index = this.current_student_mtss.communications.findIndex(
          (u) => {
            return u.id == update.id;
          }
        );
        if (index === -1) {
          this.current_student_mtss.communications.push(update);
        } else {
          this.current_student_mtss.communications.splice(index, 1, update);
        }
      } else if (update.update_type == "Progress Monitoring") {
        const index = this.current_student_mtss.progress.findIndex((u) => {
          return u.id == update.id;
        });
        if (index === -1) {
          this.current_student_mtss.progress.push(update);
        } else {
          this.current_student_mtss.progress.splice(index, 1, update);
        }
      }
    },

    deleteStudentActivity(activity) {
      if (
        activity.student_id.trim() !== this.current_student.STUDENT_ID.trim()
      ) {
        return;
      }
      const index = this.current_student_mtss.activities.findIndex((a) => {
        return a.id == activity.id;
      });
      if (index !== -1) {
        this.current_student_mtss.activities.splice(index, 1);
      }
    },

    deleteStudentUpdate(update) {
      if (update.student_id.trim() !== this.current_student.STUDENT_ID.trim()) {
        return;
      }

      // Check if update belongs to activity. If so, update the activity.
      if (update.activity_id !== null) {
        const index = this.current_student_mtss.activities.findIndex((a) => {
          return a.id == update.activity_id;
        });
        if (index !== -1) {
          const uIndex = this.current_student_mtss.activities[
            index
          ].updates.findIndex((u) => {
            return u.id == update.id;
          });

          if (uIndex !== -1) {
            this.current_student_mtss.activities[index].updates.splice(
              uIndex,
              1
            );
          }
        }
        return;
      }

      // Update belongs to student.
      if (update.update_type == "Communication") {
        const index = this.current_student_mtss.communications.findIndex(
          (u) => {
            return u.id == update.id;
          }
        );
        if (index !== -1) {
          this.current_student_mtss.communications.splice(index, 1);
        }
      } else if (update.update_type == "Progress Monitoring") {
        const index = this.current_student_mtss.progress.findIndex((u) => {
          return u.id == update.id;
        });
        if (index !== -1) {
          this.current_student_mtss.progress.splice(index, 1);
        }
      }
    },
  },
});
